import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import type { StoreState } from "./types";
import steps from "@config/steps";
import { STEP_ID, STEP_IS_VISIBLE } from "@/types/steps.types";

const initialState: Omit<StoreState, "actions"> = {
  config: null,
  steps: [],
  currentStep: undefined,
  completedSteps: [],
  isManualInput: true,
  wasFromAccount: false,
  prescriptionData: {
    products: [],
  },
  isInsuranceActive: false,
  isInsuranceLoading: false,
  isLoading: false,
  error: null,
  isWidgetBlocked: false,
};

export const useStore = create<StoreState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      actions: {
        initializeStore: async (config) => {
          set({ config }, false, "initializeStore");

          await get().actions.generateSteps(config);
          get().steps[0];
          const firstStep = get().steps[0];
          if (firstStep) {
            get().actions.setCurrentStep(firstStep);
          }
        },

        generateSteps: async (config) => {
          const cfg = config ?? get().config;
          const singleProductSelected =
            (cfg?.contactLensesData?.OD?.product &&
              !cfg.contactLensesData?.OS?.product) ||
            (cfg?.contactLensesData?.OS?.product &&
              !cfg?.contactLensesData?.OD?.product);

          const userLogged = cfg?.loginModule?.isLoggedIn() || false;
          const productID =
            cfg?.contactLensesData?.OD?.product?.catalogEntryIdentifier
              ?.uniqueId ||
            cfg?.contactLensesData?.OS?.product?.catalogEntryIdentifier
              ?.uniqueId;
          let userSavedPrescriptionForProduct = null;
          if (userLogged) {
            const userHasSavedPrescription =
              await cfg?.prescriptionModule?.retrieveFromMyAccount(
                productID || "",
              );
            userSavedPrescriptionForProduct = userHasSavedPrescription?.filter(
              (prescription: any) => {
                const ODId =
                  cfg?.contactLensesData?.OD?.product?.catalogEntryIdentifier
                    ?.uniqueId;
                const OSId =
                  cfg?.contactLensesData?.OS?.product?.catalogEntryIdentifier
                    ?.uniqueId;
                if (ODId && OSId) {
                  return (
                    ODId.toString() ===
                      prescription.rightCatentryParentId.toString() &&
                    OSId.toString() ===
                      prescription.leftCatentryParentId.toString()
                  );
                } else if (ODId && !OSId) {
                  return (
                    ODId.toString() ===
                    prescription.rightCatentryParentId.toString()
                  );
                } else if (!ODId && OSId) {
                  return (
                    OSId.toString() ===
                    prescription.leftCatentryParentId.toString()
                  );
                }
              },
            );
          }

          let filteredSteps = steps.filter((step) => {
            if (!step.isVisible) return true;
            return step.isVisible?.every((visible) => {
              switch (visible) {
                case STEP_IS_VISIBLE.USER_LOGGED:
                  return userLogged;
                case STEP_IS_VISIBLE.PRODUCT_SELECTED:
                  return singleProductSelected;
                case STEP_IS_VISIBLE.USER_HAS_SAVED_PRESCRIPTION:
                  return (
                    userLogged &&
                    userSavedPrescriptionForProduct &&
                    userSavedPrescriptionForProduct.length > 0
                  );
                default:
                  return true;
              }
            });
          });

          // i check if the user is editing from cart
          const isEditingFromCart =
            cfg?.orderId &&
            ((cfg?.contactLensesData?.OD &&
              cfg?.contactLensesData?.OD?.product?.orderItemId) ||
              !cfg?.contactLensesData?.OD) &&
            ((cfg?.contactLensesData?.OS &&
              cfg?.contactLensesData?.OS?.product?.orderItemId) ||
              !cfg?.contactLensesData?.OS);

          if (isEditingFromCart) {
            get().actions.setManualInput(true);
            filteredSteps = filteredSteps.filter((step) => {
              return step.id === STEP_ID.ADVANCED_PRESCRIPTION;
            });
          }

          // we check if the insurance is already enabled and activate inside the configurator
          if (cfg?.insuranceModule?.isEnabled) {
            get().actions.setIsInsuranceActive(
              cfg?.insuranceModule?.isEnabled(),
            );
          }

          get().actions.setSteps(filteredSteps);
        },

        setConfig: (config) => {
          set({ config }, false, "setConfig");
        },

        setSteps: (steps) => {
          set({ steps }, false, "setSteps");
        },

        setCurrentStep: (step) => {
          set({ currentStep: step }, false, "setCurrentStep");
        },

        setManualInput: (data) => {
          set({ isManualInput: data }, false, "setManualInput");
        },

        setWasFromAccount: (data) => {
          set({ wasFromAccount: data }, false, "setWasFromAccount");
        },

        markStepCompleted: (step) => {
          set(
            (state) => ({
              completedSteps: state.completedSteps.includes(step)
                ? state.completedSteps
                : [...state.completedSteps, step],
            }),
            false,
            "markStepCompleted",
          );
        },

        removeCompletedStep: (step) => {
          set(
            (state) => ({
              completedSteps: state.completedSteps.filter((s) => s !== step),
            }),
            false,
            "removeCompletedStep",
          );
        },

        updatePrescriptionData: (data) => {
          set(
            (state) => ({
              prescriptionData: {
                ...state.prescriptionData,
                ...data,
              },
            }),
            false,
            "updatePrescriptionData",
          );
        },

        setIsInsuranceLoading: (loading) => {
          set({ isInsuranceLoading: loading }, false, "setIsInsuranceLoading");
        },

        setIsInsuranceActive: (active: boolean) => {
          set({ isInsuranceActive: active }, false, "setIsInsuranceActive");
        },

        setLoading: (loading) => {
          set({ isLoading: loading }, false, "setLoading");
        },

        setWidgetBlocked: (blocked) => {
          set({ isWidgetBlocked: blocked }, false, "setWidgetBlocked");
        },

        setError: (error) => {
          set({ error }, false, "setError");
        },

        resetState: () => {
          set({ ...initialState }, false, "resetState");
        },
      },
    }),
    {
      name: "ConfigStore",
      enabled: import.meta.env.DEV,
    },
  ),
);

export const useStoreActions = () => useStore((state) => state.actions);

export const useStepStore = () =>
  useStore(
    useShallow((state) => ({
      steps: state.steps,
      currentStep: state.currentStep,
      completedSteps: state.completedSteps,
      isManualInput: state.isManualInput,
      wasFromAccount: state.wasFromAccount,
      isInsuranceActive: state.isInsuranceActive,
      isInsuranceLoading: state.isInsuranceLoading,
      isLoading: state.isLoading,
      isWidgetBlocked: state.isWidgetBlocked,
    })),
  );

export const useConfigStore = () =>
  useStore(
    useShallow((state) => ({
      config: state.config,
      isLoading: state.isLoading,
      error: state.error,
    })),
  );
